import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { KINDS, listsForPerson, type ChecklistKind, type RoleKey, type EngagementType } from '@pti/domain';
import { api } from '../api';
import { useCan } from '../hooks';
import { fmtDate } from '../format';
import type { Person, Run, SettingsData, Task } from '../types';
import { Avatar, ErrorNote, Icon, Loading, Modal, Pill, Section, TASK_TONE, toast } from '../components/ui';
import { Change, ConfirmFlow } from '../components/Confirm';
import { ChangeDatesDialog } from './Lifecycle';

export const taskLabel = (t: Task) =>
  t.state === 'done' ? `Done ${fmtDate(t.doneAt)}` : t.state === 'failed' ? 'Automatic step failed' : t.state === 'overdue' ? `Overdue ${-(t.days ?? 0)} d`
    : t.state === 'today' ? 'Due today' : t.state === 'soon' ? `In ${t.days} d` : t.state === 'no_date' ? 'Date not set' : fmtDate(t.dueDate);

export function Checklists() {
  const { id } = useParams();
  const [search] = useSearchParams();
  const nav = useNavigate();
  const can = useCan();
  const [status, setStatus] = useState<'open' | 'closed' | 'all'>('open');
  const [starting, setStarting] = useState<string | null>(search.get('start'));
  const runs = useQuery({ queryKey: ['runs', status], queryFn: () => api.get<Run[]>(`/api/checklists?status=${status}`) });
  const openId = id ? Number(id) : runs.data?.[0]?.id;
  useEffect(() => { setStarting(search.get('start')); }, [search]);
  if (runs.isLoading) return <Loading />;
  if (runs.error) return <ErrorNote error={runs.error} />;
  return (
    <div className="page">
      <div className="page-h">
        <div><p className="eyebrow">Across the lifecycle</p><h1>Onboarding & offboarding</h1>
          <p className="lede">Each checklist is the All staff list plus the list for the person’s role. Due dates follow their key dates.</p></div>
        <div className="row gap wrap-row">
          {can('settings:checklists') && <button className="btn" onClick={() => nav('/settings')}>Edit lists</button>}
          {can('checklists:write') && <button className="btn primary" onClick={() => setStarting('')}>+ Start a checklist</button>}
        </div>
      </div>
      <div className="chips">
        {(['open', 'closed', 'all'] as const).map((s) => <button key={s} className={`chip${status === s ? ' on' : ''}`} onClick={() => setStatus(s)}>{s === 'open' ? 'Running' : s === 'closed' ? 'Finished' : 'All'}</button>)}
      </div>
      {runs.data!.length === 0 ? <p className="empty">No checklists here yet. Start one when an offer is accepted or someone resigns.</p> : (
        <div className="cl-cards">
          {runs.data!.map((r) => (
            <button key={r.id} className={`cl-card${r.id === openId ? ' on' : ''}`} onClick={() => nav(`/checklists/${r.id}`)}>
              <span className="row between"><span className="who"><Avatar name={r.person.name} size={30} /><strong>{r.person.name}</strong></span><Pill tone={r.kind === 'onboarding' ? 'accent' : 'warn'}>{KINDS[r.kind].label}</Pill></span>
              <span className="small dim">{KINDS[r.kind].anchors.map((a) => `${a.label} ${fmtDate(r.anchors[a.key])}`).join(' · ')}</span>
              <span className="prog wide"><span style={{ width: `${(r.summary.done / Math.max(1, r.summary.total)) * 100}%` }} /></span>
              <span className="row between small"><span>{r.summary.done} of {r.summary.total} done</span>{r.summary.overdue ? <span className="tone-bad">{r.summary.overdue} overdue</span> : r.summary.next ? <span className="dim">Next: {fmtDate(r.summary.next)}</span> : <span className="tone-ok">Complete</span>}</span>
            </button>
          ))}
        </div>
      )}
      {openId && <RunDetail id={openId} />}
      {starting !== null && <StartModal presetPerson={starting || undefined} onClose={() => { setStarting(null); if (search.get('start')) nav('/checklists'); }} />}
    </div>
  );
}

export function RunDetail({ id, portal }: { id: number; portal?: boolean }) {
  const qc = useQueryClient();
  const can = useCan();
  const [show, setShow] = useState('all');
  const [cancelling, setCancelling] = useState(false);
  const [redating, setRedating] = useState(false);
  const q = useQuery({ queryKey: ['run', id], queryFn: () => api.get<Run>(`/api/checklists/${id}`) });
  const refresh = (r: Run) => { qc.setQueryData(['run', id], r); void qc.invalidateQueries({ queryKey: ['runs'] }); void qc.invalidateQueries({ queryKey: ['overview'] }); };
  const tick = useMutation({ mutationFn: ({ taskId, done }: { taskId: number; done: boolean }) => api.send<Run>('POST', `/api/checklists/${id}/tasks/${taskId}`, { done }), onSuccess: refresh, onError: (e) => toast((e as Error).message) });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorNote error={q.error} />;
  const r = q.data!;
  const kind = KINDS[r.kind];
  const visible = r.tasks.filter((t) => show === 'all' || (show === 'open' ? t.state !== 'done' : show === 'emp' ? t.visible : t.owner === show));
  return (
    <Section title={`${r.person.name} — ${kind.label.toLowerCase()}`} aside={<span className="ref">{r.code}</span>}>
      {!portal && (
        <div className="anchor-row">
          {kind.anchors.map((a) => <div key={a.key} className="field"><span>{a.label}</span><strong>{fmtDate(r.anchors[a.key]) || '—'}</strong></div>)}
          {can('checklists:write') && r.status === 'open' && <button className="btn sm" onClick={() => setRedating(true)}>Change dates</button>}
          <p className="small dim">Change a key date and every open task moves with it.</p>
        </div>
      )}
      {r.status !== 'open' && <div className="callout subtle small">This checklist is {r.status}.{r.note ? ` ${r.note}` : ''}</div>}
      {r.status === 'open' && r.note && <div className="callout warn small">{r.note}</div>}
      {!portal && (
        <div className="chips">
          {[['all', 'All tasks'], ['open', 'Open'], ['emp', r.kind === 'onboarding' ? 'Visible to new starter' : 'Visible to leaver'], ['Director', 'Director'], ['RTO Manager', 'RTO Manager'], ['Admin Officer', 'Admin Officer']]
            .map(([k, l]) => <button key={k} className={`chip${show === k ? ' on' : ''}`} onClick={() => setShow(k!)}>{l}</button>)}
        </div>
      )}
      {kind.phases.map((ph) => {
        const rows = visible.filter((t) => t.phase === ph);
        if (!rows.length) return null;
        return (
          <div key={ph} className="chk-group">
            <h4>{ph}</h4>
            <ul className="chk tasks">
              {rows.map((t) => {
                const mine = portal && (t.owner === 'New starter' || t.owner === 'Leaver');
                const disabled = r.status !== 'open' || (portal ? !mine : (t.owner === 'System' && !can('settings:checklists')));
                return (
                  <li key={t.id} className={t.state === 'done' ? 'is-done' : ''}>
                    <input type="checkbox" id={`t${t.id}`} checked={t.state === 'done'} disabled={disabled || tick.isPending} onChange={(e) => tick.mutate({ taskId: t.id, done: e.target.checked })} />
                    <label htmlFor={`t${t.id}`} className="chk-t">
                      <strong>{t.title}{t.critical && <span className="crit" title="Critical — escalates if overdue">!</span>}</strong>
                      <span className="small dim">{t.owner === 'System' ? 'Automatic' : t.owner}{t.listName !== 'All staff' ? ` · ${t.listName} list` : ''}{t.ref ? ` · ${t.ref}` : ''}{t.doneBy && t.state === 'done' ? ` · ticked by ${t.doneBy}` : ''}</span>
                      {t.autoError && t.state !== 'done' && <span className="small tone-warn">{t.autoError}</span>}
                    </label>
                    <span className="chk-s"><span className="row gap-s">{!portal && t.visible && <span className="eye" title="Visible to the employee"><Icon n="eye" s={14} /></span>}<Pill tone={TASK_TONE[t.state]}>{taskLabel(t)}</Pill></span>
                      {t.dueDate && t.state !== 'later' && <span className="small dim num">{fmtDate(t.dueDate)}</span>}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
      {!portal && can('checklists:write') && r.status === 'open' && <button className="link danger" onClick={() => setCancelling(true)}>{r.kind === 'onboarding' ? 'Offer withdrawn — cancel this checklist' : 'Cancel this checklist'}</button>}
      {cancelling && <CancelModal run={r} onClose={() => setCancelling(false)} />}
      {redating && (r.kind === 'onboarding'
        ? <ChangeDatesDialog person={{ id: r.person.id, name: r.person.name, givenName: r.person.givenName, offerAcceptedDate: r.anchors.offer ?? null, startDate: r.anchors.start ?? null } as Person} onClose={() => { setRedating(false); void qc.invalidateQueries({ queryKey: ['run', id] }); }} />
        : <OffboardingDatesDialog run={r} onClose={() => setRedating(false)} onDone={refresh} />)}
    </Section>
  );
}

function OffboardingDatesDialog({ run, onClose, onDone }: { run: Run; onClose: () => void; onDone: (r: Run) => void }) {
  const [d, setD] = useState<Record<string, string>>(Object.fromEntries(KINDS.offboarding.anchors.map((a) => [a.key, run.anchors[a.key] ?? ''])));
  const changed = KINDS.offboarding.anchors.filter((a) => (d[a.key] || null) !== (run.anchors[a.key] ?? null));
  return (
    <ConfirmFlow<Run>
      title={`Change ${run.person.givenName}'s offboarding dates`}
      onClose={onClose}
      canContinue={changed.length > 0 && !!d.last}
      review={() => (
        <>
          <p>You are about to change {run.person.name}’s offboarding dates. Every open task moves with them{changed.some((a) => a.key === 'last') ? ', including the automatic account lock on the last day' : ''}:</p>
          <ul className="plain-list">{KINDS.offboarding.anchors.map((a) => <Change key={a.key} label={a.label} from={fmtDate(run.anchors[a.key])} to={fmtDate(d[a.key] || null)} />)}</ul>
        </>
      )}
      confirmLabel="Yes, change the dates"
      onConfirm={() => api.send<Run>('PATCH', `/api/checklists/${run.id}/anchors`, Object.fromEntries(changed.map((a) => [a.key, d[a.key] || null])))}
      onDone={(r) => { onDone(r); toast('Dates changed — due dates moved'); }}
    >
      <div className="two">{KINDS.offboarding.anchors.map((a) => (
        <label key={a.key} className="field" htmlFor={`od-${a.key}`}><span>{a.label}</span><input id={`od-${a.key}`} type="date" className="txt" value={d[a.key]} onChange={(e) => setD({ ...d, [a.key]: e.target.value })} /></label>
      ))}</div>
    </ConfirmFlow>
  );
}

function CancelModal({ run, onClose }: { run: Run; onClose: () => void }) {
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const m = useMutation({ mutationFn: () => api.send('POST', `/api/checklists/${run.id}/cancel`, { reason }), onSuccess: () => { void qc.invalidateQueries(); toast('Checklist cancelled'); onClose(); } });
  return (
    <Modal title="Cancel checklist" onClose={onClose}>
      <p>{run.kind === 'onboarding' ? `If ${run.person.givenName} has a pre-start Microsoft account that has not been used, it will be deleted.` : 'Open tasks will stop and no further reminders will be sent.'}</p>
      <label className="field" htmlFor="cx-reason"><span>Reason</span><input id="cx-reason" className="txt" value={reason} onChange={(e) => setReason(e.target.value)} /></label>
      {m.error && <ErrorNote error={m.error} />}
      <div className="row gap"><button className="btn danger-fill" disabled={reason.trim().length < 3 || m.isPending} onClick={() => m.mutate()}>Cancel checklist</button><button className="btn" onClick={onClose}>Keep it</button></div>
    </Modal>
  );
}

function StartModal({ onClose, presetPerson }: { onClose: () => void; presetPerson?: string }) {
  const qc = useQueryClient();
  const can = useCan();
  const nav = useNavigate();
  const people = useQuery({ queryKey: ['people'], queryFn: () => api.get<Person[]>('/api/people') });
  const settings = useQuery({ queryKey: ['settings'], queryFn: () => api.get<SettingsData>('/api/settings/checklists') });
  const [kind, setKind] = useState<ChecklistKind>('onboarding');
  const [who, setWho] = useState(presetPerson ?? '');
  const [anchors, setAnchors] = useState<Record<string, string>>({});
  const [override, setOverride] = useState<string[] | null>(null);
  const m = useMutation({
    mutationFn: () => api.send<{ id: number }>('POST', '/api/checklists', { personId: who, kind, anchors: Object.fromEntries(KINDS[kind].anchors.map((a) => [a.key, anchors[a.key] || null])), listKeys: override ?? undefined }),
    onSuccess: (r) => { void qc.invalidateQueries(); toast('Checklist started'); onClose(); nav(`/checklists/${r.id}`); },
  });
  const person = people.data?.find((p) => p.id === who);
  const lists = (settings.data?.lists ?? []).filter((l) => l.kind === kind);
  const auto = person ? listsForPerson({ roleKey: person.roleKey as RoleKey | null, engagementType: person.engagementType as EngagementType }, lists as unknown as Parameters<typeof listsForPerson>[1]).map((l) => l.key) : [];
  const chosen = override ?? auto;
  return (
    <Modal title="Start a checklist" onClose={onClose}>
      <div className="seg" role="radiogroup" aria-label="Checklist type">
        {(['onboarding', 'offboarding'] as const).map((k) => <button key={k} role="radio" aria-checked={kind === k} disabled={k === 'offboarding' && !can('accounts:lock')} title={k === 'offboarding' && !can('accounts:lock') ? 'Director or RTO Manager — it schedules the account lock' : undefined} className={kind === k ? 'on' : ''} onClick={() => { setKind(k); setOverride(null); }}>{KINDS[k].label}</button>)}
      </div>
      <label className="field" htmlFor="st-who"><span>Person</span>
        <select id="st-who" value={who} onChange={(e) => { setWho(e.target.value); setOverride(null); }}>
          <option value="" disabled>Choose…</option>
          {people.data?.map((p) => <option key={p.id} value={p.id}>{p.name}{p.positionTitle ? ` — ${p.positionTitle}` : ''}</option>)}
        </select>
      </label>
      <div className="two">
        {KINDS[kind].anchors.map((a) => (
          <label key={a.key} className="field" htmlFor={`st-${a.key}`}><span>{a.label}</span><input id={`st-${a.key}`} type="date" className="txt" value={anchors[a.key] ?? ''} onChange={(e) => setAnchors({ ...anchors, [a.key]: e.target.value })} /></label>
        ))}
      </div>
      {person && (
        <div className="field"><span>Lists included <span className="dim">(from their role — change if needed)</span></span>
          <div className="chips">
            {lists.map((l) => <button key={l.key} disabled={l.isCore} className={`chip${chosen.includes(l.key) ? ' on' : ''}`} onClick={() => setOverride(chosen.includes(l.key) ? chosen.filter((x) => x !== l.key) : [...chosen, l.key])}>{l.name}</button>)}
          </div>
        </div>
      )}
      {kind === 'onboarding' && person && !person.personalEmail && <div className="callout warn small">Add a personal email to {person.givenName}’s record so the account activation pass can be sent.</div>}
      {m.error && <ErrorNote error={m.error} />}
      <div className="row gap"><button className="btn primary" disabled={!who || !anchors[kind === 'onboarding' ? 'offer' : 'last'] || m.isPending} onClick={() => m.mutate()}>Start checklist</button><button className="btn" onClick={onClose}>Cancel</button></div>
    </Modal>
  );
}
